// TransactionCard.tsx - Final Corrected Version
import React from "react";
import RevenueService from "../../../services/revenueService";
import { ColorTheme, TransactionCardProps } from "../types/revenue.types";
import { useAuth } from "../../../hooks/useAuth";

export const TransactionCard: React.FC<TransactionCardProps> = ({
  transaction,
  colorTheme,
}) => {
  const { isDoctor } = useAuth();

  // Extract data from relationships (with safe fallbacks)
  const serviceName = transaction.service?.name || "Unknown Service";
  const patientName = transaction.appointment?.patient
    ? `${transaction.appointment.patient.first_name || ""} ${transaction.appointment.patient.last_name || ""}`.trim() ||
      "Unknown Patient"
    : "Unknown Patient";
  const providerName = transaction.service?.provider
    ? `${transaction.service.provider.first_name || ""} ${transaction.service.provider.last_name || ""}`.trim() ||
      "Unknown Provider"
    : "Unknown Provider";

  return (
    <div
      style={{
        padding: "16px",
        borderBottom: `1px solid ${colorTheme.gray100}`,
        transition: "background 0.2s",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = colorTheme.gray50;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = "transparent";
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
        }}
      >
        <div style={{ flex: 1 }}>
          {/* Service Name */}
          <div
            style={{
              fontSize: "16px",
              fontWeight: "600",
              color: colorTheme.gray900,
              marginBottom: "4px",
            }}
          >
            {serviceName}
          </div>

          {/* Patient Info */}
          <div
            style={{
              fontSize: "14px",
              color: colorTheme.gray600,
              marginBottom: "8px",
            }}
          >
            {patientName}
          </div>

          {/* Transaction Details */}
          <div
            style={{
              display: "flex",
              gap: "16px",
              fontSize: "12px",
              color: colorTheme.gray500,
            }}
          >
            <span>
              📅 {new Date(transaction.transaction_date).toLocaleDateString()}
            </span>
            {!isDoctor() && <span>👨‍⚕️ {providerName}</span>}
          </div>

          {/* Revenue Breakdown - Different for Doctors vs Admins */}
          <div
            style={{
              display: "flex",
              gap: "12px",
              marginTop: "8px",
              flexWrap: "wrap",
            }}
          >
            {isDoctor() ? (
              // DOCTOR VIEW: Only show their earnings
              <>
                <span
                  style={{
                    fontSize: "12px",
                    background: "#dbeafe",
                    color: colorTheme.primary,
                    padding: "4px 8px",
                    borderRadius: "4px",
                    fontWeight: "500",
                  }}
                >
                  My Earnings:{" "}
                  {RevenueService.formatCurrency(transaction.provider_amount)}
                </span>
              </>
            ) : (
              // ADMIN VIEW: Show full breakdown
              <>
                <span
                  style={{
                    fontSize: "12px",
                    background: "#dbeafe",
                    color: colorTheme.primary,
                    padding: "4px 8px",
                    borderRadius: "4px",
                    fontWeight: "500",
                  }}
                >
                  Clinic:{" "}
                  {RevenueService.formatCurrency(transaction.clinic_amount)}
                </span>
                <span
                  style={{
                    fontSize: "12px",
                    background: "#d1fae5",
                    color: colorTheme.success,
                    padding: "4px 8px",
                    borderRadius: "4px",
                    fontWeight: "500",
                  }}
                >
                  Provider:{" "}
                  {RevenueService.formatCurrency(transaction.provider_amount)}
                </span>
                {transaction.charity_amount > 0 && (
                  <span
                    style={{
                      fontSize: "12px",
                      background: "#fce7f3",
                      color: colorTheme.charity,
                      padding: "4px 8px",
                      borderRadius: "4px",
                      fontWeight: "500",
                    }}
                  >
                    Charity:{" "}
                    {RevenueService.formatCurrency(transaction.charity_amount)}
                  </span>
                )}
              </>
            )}
          </div>
        </div>

        {/* Total Amount */}
        <div style={{ textAlign: "right" }}>
          <div
            style={{
              fontSize: "20px",
              fontWeight: "700",
              color: isDoctor() ? colorTheme.success : colorTheme.gray900,
            }}
          >
            {isDoctor()
              ? RevenueService.formatCurrency(transaction.provider_amount)
              : RevenueService.formatCurrency(transaction.total_amount)}
          </div>
          {!isDoctor() && (
            <div
              style={{
                fontSize: "12px",
                color: colorTheme.gray500,
                marginTop: "4px",
              }}
            >
              Total Revenue
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
